import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

import { environment } from '../../environments/environment';
import { PWAService } from './pwa.service';
import { LOGGER } from '../oidc/shared/logger';

/**
 * Why a session ended. Sent verbatim as the `type` field, so the values are API — the
 * backend has to know a value before it can aggregate it.
 *
 * `SCOPE_REJECTED` is the odd one out: it is not a sign-out at all, the session survives
 * it. It rides this channel because it answers the same question — why did a session stop
 * working — and because older releases DID end the session over it and counted it here as
 * `REFRESH_TOKEN_INVALID`. Keeping both in one series is what makes them comparable.
 */
export enum SignOutType {
  REFRESH_TOKEN_INVALID = 'refresh_token_invalid',
  USER_INITIATED = 'user_initiated',
  SCOPE_REJECTED = 'scope_rejected'
}

const API_LOGOUT_STATISTICS = `${environment.apiUrl}/logout-statistics`;

/**
 * Reports *that* a session ended and for which reason, together with app version and
 * platform — nothing that identifies the user.
 *
 * The point of this is `REFRESH_TOKEN_INVALID`: a user being thrown out without having
 * asked for it looks exactly like a working app from the outside, and this counter is
 * the only thing that made the wave of "spontaneous logouts" visible back then. Reading
 * it means comparing it against the user-initiated ones — an absolute number says little
 * on its own, the ratio and its jumps between releases say everything.
 *
 * Fire-and-forget on purpose: a failed report must never hold up or break a logout, which
 * is why nothing here returns something a caller could await or fail on.
 */
@Injectable({ providedIn: 'root' })
export class LogoutStatisticsService {
  private readonly http = inject(HttpClient);
  private readonly log = inject(LOGGER).scope('logout-statistics');

  report(type: SignOutType): void {
    void this.send(type);
  }

  private async send(type: SignOutType): Promise<void> {
    try {
      const [appInfo, deviceInfo] = await Promise.all([
        Capacitor.isNativePlatform() ? App.getInfo() : PWAService.getInfo(),
        Device.getInfo(),
      ]);

      const body = new URLSearchParams();
      body.set('type', type);
      body.set('version', appInfo.version);
      body.set('build', appInfo.build);
      body.set('os', deviceInfo.operatingSystem);
      body.set('os_version', deviceInfo.osVersion);
      body.set('webViewVersion', deviceInfo.webViewVersion ?? 'unknown');

      const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
      this.http.post(API_LOGOUT_STATISTICS, body, { headers }).subscribe({
        next: () => this.log.debug('reported', { type }),
        error: (error: unknown) => this.log.warn('report failed', { type, error: String(error) }),
      });
    } catch (error: unknown) {
      this.log.warn('report could not be assembled', { type, error: String(error) });
    }
  }
}
